import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, BookOpen, MagnifyingGlass, ListBullets, Lightning, Warning, Wrench, Cards, Scroll, GameController, Globe, ArrowSquareOut
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';
import { useAiConsent } from '../components/AiConsentModal';
import GameRatingWidget from '../components/GameRatingWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_ICONS = {
  core: BookOpen, building: Wrench, actions: Lightning, pieces: GameController,
  special: Cards, resources: ListBullets, economy: ListBullets, cards: Cards,
  robber: Warning, combat: Lightning, endgame: Scroll, advanced: Scroll,
  mechanics: Wrench, control: ListBullets, score: ListBullets,
};

export default function RulesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ensureConsent, AiConsentModalElement } = useAiConsent();

  const [games, setGames] = useState([]);
  const [game, setGame] = useState(null);
  const [rules, setRules] = useState([]);
  const [setupGuide, setSetupGuide] = useState(null);
  const [quickRef, setQuickRef] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeTab, setActiveTab] = useState('rules');
  const [question, setQuestion] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [webRules, setWebRules] = useState([]);
  const [webSources, setWebSources] = useState([]);
  const [webFetching, setWebFetching] = useState(false);
  const [webGeneratedAt, setWebGeneratedAt] = useState(null);

  const isDetail = !!slug;

  useEffect(() => {
    // Reset ALL per-game state when navigating to a different game.
    // Without this, the previous game's rules, web-fetched rules, scenarios,
    // recent questions, etc. briefly remain visible (or worse, persist if
    // the next fetch fails) — that's the "Game A rules appear when viewing
    // Game B" contamination bug.
    if (slug) {
      setGame(null);
      setRules([]);
      setSetupGuide(null);
      setQuickRef(null);
      setScenarios([]);
      setSearchQuery('');
      setSearchResults([]);
      setRecentQuestions([]);
      setAnswer(null);
      setQuestion('');
      setCatFilter('');
      setWebRules([]);
      setWebSources([]);
      setWebGeneratedAt(null);
      setLoading(true);
      fetchGameData();
    } else {
      fetchGames();
    }
  }, [slug]);

  const fetchGames = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/games`, { withCredentials: true });
      setGames(data.filter(g => g.slug));
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchGameData = async () => {
    try {
      const [gameRes, rulesRes, setupRes, qrRes, scenariosRes, questionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/games/${slug}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/games/${slug}/rules`, { withCredentials: true }),
        axios.get(`${API_URL}/api/games/${slug}/setup-guide`, { withCredentials: true }).catch(() => ({ data: { steps: [] } })),
        axios.get(`${API_URL}/api/games/${slug}/quick-reference`, { withCredentials: true }).catch(() => ({ data: { content: '' } })),
        axios.get(`${API_URL}/api/games/${slug}/scenarios`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/rules/${slug}?limit=10`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setGame(gameRes.data);
      setRules(Array.isArray(rulesRes.data) ? rulesRes.data : []);
      setSetupGuide(setupRes.data);
      setQuickRef(qrRes.data);
      setScenarios(Array.isArray(scenariosRes.data) ? scenariosRes.data : []);
      setRecentQuestions(Array.isArray(questionsRes.data) ? questionsRes.data : []);
    } catch (err) { toast.error('Failed to load game'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const t = setTimeout(async () => {
        try {
          const { data } = await axios.get(`${API_URL}/api/games/${slug}/rules/search?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
          setSearchResults(data);
        } catch (err) { /* silent */ }
      }, 300);
      return () => clearTimeout(t);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, slug]);

  const handleAskQuestion = async () => {
    if (!question.trim() || !game) return;
    setAskLoading(true);
    setAnswer(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/rules/questions`, {
        game_name: game.name, question_text: question
      }, { withCredentials: true });
      setAnswer(data);
      setRecentQuestions(prev => [data, ...prev].slice(0, 10));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get answer');
    } finally { setAskLoading(false); }
  };

  const fetchRulesFromWeb = async () => {
    if (webFetching) return;
    // Apple 5.1.1(i)/5.1.2(i): request explicit consent before sending data to OpenAI.
    const ok = await ensureConsent();
    if (!ok) return;
    setWebFetching(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/games/${slug}/rules/web-fetch`, {}, { withCredentials: true });
      setWebRules(Array.isArray(data?.rules) ? data.rules : []);
      setWebSources(Array.isArray(data?.sources) ? data.sources : []);
      setWebGeneratedAt(data?.generated_at);
      if (!data?.rules?.length) {
        toast.message('No rules generated — please try again.');
      } else {
        toast.success(data?.cached ? 'Loaded cached web rules' : `Generated ${data.rules.length} rules`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch rules');
    } finally {
      setWebFetching(false);
    }
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  // Game List View
  if (!isDetail) {
    return (
      <div className="app-container" data-testid="rules-list-page">
        <header className="p-6 pb-3">
          <h1 className="text-xl font-bold text-white flex items-center gap-2 mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <BookOpen size={24} weight="duotone" className="text-[#9D4CDD]" />
            Game Rules
          </h1>
          <p className="text-[#71717A] text-xs">Browse rules, setup guides & quick references</p>
        </header>
        <div className="px-6 pb-32 space-y-2">
          {games.map((g, i) => (
            <motion.button key={g.slug} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              onClick={() => navigate(`/rules/${g.slug}`)}
              className="w-full text-left p-4 rounded-xl bg-[#0F0F16] border border-white/5 hover:border-[#9D4CDD]/30 transition-all flex items-center gap-3"
              data-testid={`game-card-${g.slug}`}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center flex-shrink-0">
                <GameController size={20} weight="duotone" className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white text-sm font-medium">{g.name}</h3>
                <div className="flex items-center gap-2 text-[9px] text-[#71717A]">
                  <span>{g.min_players}-{g.max_players} players</span>
                  <span className="w-1 h-1 rounded-full bg-[#71717A]" />
                  <span className="capitalize">{g.complexity}</span>
                  <span className="w-1 h-1 rounded-full bg-[#71717A]" />
                  <span className="capitalize">{g.category}</span>
                </div>
              </div>
            </motion.button>
          ))}
          {games.length === 0 && <p className="text-center text-[#71717A] py-8">No games found</p>}
        </div>
        <BottomNav />
      </div>
    );
  }

  // Game Detail View
  const categories = [...new Set(rules.map(r => r.category).filter(Boolean))];
  const filteredRules = catFilter ? rules.filter(r => r.category === catFilter) : rules;
  const displayRules = searchQuery.length >= 2 ? searchResults : filteredRules;

  return (
    <div className="app-container" data-testid="rules-detail-page">
      {AiConsentModalElement}
      <header className="p-6 pb-2">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/rules')} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>{game?.name || slug}</h1>
            <p className="text-[#71717A] text-[10px]">{game?.min_players}-{game?.max_players} players | {game?.complexity} | {game?.category}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search rules..."
            className="w-full pl-9 pr-4 py-2.5 bg-[#181824] rounded-xl text-white text-xs border border-white/5 focus:border-[#9D4CDD]/50 outline-none"
            data-testid="rules-search" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-none -mx-6 px-6 pb-1">
          {['rules', 'setup', 'quick-ref', 'scenarios', 'ask'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${activeTab === t ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'}`}
              data-testid={`tab-${t}`}>
              {t === 'quick-ref' ? 'Quick Ref' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="px-6 pb-32 space-y-3">
        {/* Per-user, per-game rating widget (collapsible). Aggregate shown
            in collapsed state; full 6-axis form opens on tap. */}
        {game?.slug && <GameRatingWidget slug={game.slug} gameName={game.name} />}

        {activeTab === 'rules' && (
          <>
            {/* Web-fetch action: generate or refresh rules from the internet via AI */}
            <div className="flex items-center justify-between gap-2 p-3 rounded-xl bg-gradient-to-br from-[#9D4CDD]/10 to-[#00C8D7]/10 border border-white/10">
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium flex items-center gap-1.5">
                  <Globe size={12} weight="duotone" className="text-[#00C8D7]" />
                  Get rules from the web
                </p>
                <p className="text-[#A1A1AA] text-[10px] mt-0.5">
                  {webRules.length > 0
                    ? `Showing ${webRules.length} AI-generated rules${webSources.length ? ` · ${webSources.length} source${webSources.length === 1 ? '' : 's'}` : ''}`
                    : 'AI-summarized rules from publisher pages, BoardGameGeek, and Wikipedia.'}
                </p>
              </div>
              <button
                onClick={fetchRulesFromWeb}
                disabled={webFetching}
                className="px-3 py-1.5 rounded-lg bg-[#9D4CDD] text-white text-[10px] font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                data-testid="rules-web-fetch-btn"
              >
                {webFetching ? 'Fetching…' : webRules.length > 0 ? 'Refresh' : 'Generate'}
              </button>
            </div>

            {/* Sources strip (only when we have web rules) */}
            {webSources.length > 0 && (
              <div className="flex gap-1.5 flex-wrap" data-testid="rules-web-sources">
                {webSources.map((s, i) => (
                  <a key={s.url + i} href={s.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#181824] border border-white/5 text-[10px] text-[#A1A1AA] hover:text-white hover:border-[#00C8D7]/40 transition-colors"
                  >
                    <ArrowSquareOut size={10} className="text-[#00C8D7]" />
                    {s.label}
                  </a>
                ))}
              </div>
            )}

            {/* Web-generated rules list */}
            {webRules.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-2 px-1">From the Web</p>
                {webRules.map((r, i) => {
                  const CatIcon = CAT_ICONS[r.category] || Globe;
                  return (
                    <div key={'web-' + i} className="p-4 rounded-xl bg-[#0F0F16] border border-[#00C8D7]/15" data-testid={`web-rule-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <CatIcon size={14} weight="duotone" className="text-[#00C8D7]" />
                        <h4 className="text-white text-sm font-medium">{r.title}</h4>
                        {r.category && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#00C8D7]/10 text-[#00C8D7] capitalize">{r.category}</span>}
                      </div>
                      <p className="text-[#A1A1AA] text-xs leading-relaxed">{r.official_text}</p>
                      {r.simplified_text && (
                        <p className="text-[#00F0FF] text-[10px] mt-2 italic">Simplified: {r.simplified_text}</p>
                      )}
                    </div>
                  );
                })}
                {displayRules.length > 0 && (
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-4 px-1">Curated Rules</p>
                )}
              </>
            )}

            {categories.length > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                <button onClick={() => setCatFilter('')} className={`px-2 py-1 rounded-lg text-[9px] ${!catFilter ? 'bg-[#9D4CDD]/10 text-[#9D4CDD]' : 'bg-[#181824] text-[#71717A]'}`}>All</button>
                {categories.map(c => (
                  <button key={c} onClick={() => setCatFilter(c)} className={`px-2 py-1 rounded-lg text-[9px] capitalize ${catFilter === c ? 'bg-[#9D4CDD]/10 text-[#9D4CDD]' : 'bg-[#181824] text-[#71717A]'}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
            <AnimatePresence>
              {displayRules.map((r, i) => {
                const CatIcon = CAT_ICONS[r.category] || BookOpen;
                return (
                  <motion.div key={r.title + i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                    className="p-4 rounded-xl bg-[#0F0F16] border border-white/5" data-testid={`rule-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <CatIcon size={14} weight="duotone" className="text-[#9D4CDD]" />
                      <h4 className="text-white text-sm font-medium">{r.title}</h4>
                      {r.category && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#9D4CDD]/10 text-[#9D4CDD] capitalize">{r.category}</span>}
                    </div>
                    <p className="text-[#A1A1AA] text-xs leading-relaxed">{r.official_text}</p>
                    {r.simplified_text && (
                      <p className="text-[#00F0FF] text-[10px] mt-2 italic">Simplified: {r.simplified_text}</p>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {displayRules.length === 0 && webRules.length === 0 && <p className="text-center text-[#71717A] py-6 text-sm">No rules found — tap &quot;Generate&quot; above to fetch from the web</p>}
          </>
        )}

        {activeTab === 'setup' && (
          <div className="space-y-2">
            {setupGuide?.steps?.length > 0 ? setupGuide.steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                className="flex gap-3 p-3 rounded-xl bg-[#0F0F16] border border-white/5" data-testid={`setup-step-${i}`}>
                <div className="w-7 h-7 rounded-full bg-[#9D4CDD] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">{s.step || i + 1}</span>
                </div>
                <div>
                  <h4 className="text-white text-xs font-medium">{s.title}</h4>
                  <p className="text-[#A1A1AA] text-[10px] mt-0.5 leading-relaxed">{s.description}</p>
                </div>
              </motion.div>
            )) : <p className="text-center text-[#71717A] py-6 text-sm">No setup guide available</p>}
          </div>
        )}

        {activeTab === 'quick-ref' && (
          <div className="p-4 rounded-xl bg-[#0F0F16] border border-white/5" data-testid="quick-ref-content">
            {quickRef?.content ? (
              <pre className="text-[#A1A1AA] text-xs leading-relaxed whitespace-pre-wrap font-mono">{quickRef.content}</pre>
            ) : (
              <p className="text-center text-[#71717A] py-6 text-sm">No quick reference available</p>
            )}
          </div>
        )}

        {activeTab === 'scenarios' && (
          <div className="space-y-2">
            {scenarios.length > 0 ? scenarios.map((s, i) => (
              <div key={i} className="p-4 rounded-xl bg-[#0F0F16] border border-white/5" data-testid={`scenario-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Warning size={14} weight="duotone" className="text-[#FACC15]" />
                  <h4 className="text-white text-xs font-medium">{s.title}</h4>
                  <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#FACC15]/10 text-[#FACC15]">{s.scenario_type}</span>
                </div>
                <p className="text-[#A1A1AA] text-[10px] mb-2">{s.description}</p>
                <div className="p-2 rounded-lg bg-[#181824] border-l-2 border-[#39FF14]">
                  <p className="text-[#39FF14] text-[10px] font-medium">Ruling: {s.ruling}</p>
                </div>
              </div>
            )) : <p className="text-center text-[#71717A] py-6 text-sm">No scenarios yet</p>}
          </div>
        )}

        {activeTab === 'ask' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input type="text" value={question} onChange={e => setQuestion(e.target.value)}
                placeholder={`Ask about ${game?.name || 'this game'}...`}
                className="flex-1 px-4 py-3 bg-[#181824] rounded-xl text-white text-xs border border-white/5 focus:border-[#9D4CDD]/50 outline-none"
                onKeyDown={e => e.key === 'Enter' && handleAskQuestion()}
                data-testid="ask-input" />
              <button onClick={handleAskQuestion} disabled={askLoading || !question.trim()}
                className="px-4 py-3 rounded-xl bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white text-xs font-medium disabled:opacity-50"
                data-testid="ask-submit">
                {askLoading ? '...' : 'Ask'}
              </button>
            </div>

            {answer && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-[#0F0F16] border border-[#9D4CDD]/30" data-testid="ai-answer">
                <div className="flex items-center gap-2 mb-2">
                  <Lightning size={14} weight="fill" className="text-[#9D4CDD]" />
                  <span className="text-[#9D4CDD] text-xs font-medium">CyBoard AI</span>
                  {answer.answer_type && (
                    <span className="text-[7px] px-1.5 py-0.5 rounded-full bg-[#FACC15]/10 text-[#FACC15]">{answer.answer_type.replace(/_/g, ' ')}</span>
                  )}
                </div>
                <p className="text-[#A1A1AA] text-xs leading-relaxed">{answer.answer_text}</p>
              </motion.div>
            )}

            {recentQuestions.length > 0 && (
              <div>
                <h4 className="text-[#71717A] text-xs font-medium mb-2">Recent Questions</h4>
                {recentQuestions.slice(0, 5).map((q, i) => (
                  <div key={i} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 mb-2">
                    <p className="text-white text-xs">{q.question_text}</p>
                    <p className="text-[#A1A1AA] text-[10px] mt-1 line-clamp-3">{q.answer_text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
